import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X, Mic, Cloud, HardDrive, Globe, CheckCircle, AlertCircle, Info, Users } from 'lucide-react';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #6b7280;
  border-radius: 4px;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const Content = styled.div`
  margin-bottom: 24px;
`;

const Description = styled.p`
  color: #6b7280;
  margin-bottom: 24px;
  line-height: 1.6;
`;

const LanguageSection = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const LanguageTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const LanguageSelector = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SpeakerSection = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const SpeakerToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
`;

const ToggleSwitch = styled.input`
  position: relative;
  width: 48px;
  height: 24px;
  background: ${props => props.checked ? '#3b82f6' : '#d1d5db'};
  border-radius: 12px;
  outline: none;
  cursor: pointer;
  transition: background 0.3s;
  appearance: none;

  &:before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.checked ? '26px' : '2px'};
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: left 0.3s;
  }
`;

const QualitySelector = styled.div`
  margin-top: 12px;
  opacity: ${props => props.enabled ? 1 : 0.5};
  pointer-events: ${props => props.enabled ? 'auto' : 'none'};
`;

const QualityOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 8px;
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e5e7eb'};
  border-radius: 6px;
  background: ${props => props.selected ? '#eff6ff' : 'white'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.selected ? '#3b82f6' : '#9ca3af'};
  }
`;

const QualityRadio = styled.input`
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
`;

const QualityDetails = styled.div`
  flex: 1;
`;

const QualityName = styled.div`
  font-weight: 500;
  color: #374151;
  font-size: 13px;
`;

const QualityDescription = styled.div`
  color: #6b7280;
  font-size: 11px;
  margin-top: 2px;
`;

const MethodGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 24px;
`;

const MethodCard = styled.div`
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e5e7eb'};
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  
  &:hover {
    border-color: ${props => props.selected ? '#3b82f6' : '#9ca3af'};
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
`;

const MethodHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const MethodTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MethodIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.color || '#3b82f6'}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color || '#3b82f6'};
`;

const MethodName = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;

const MethodStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 12px;
  
  ${props => props.status === 'available' && `
    background: #f0fdf4;
    color: #16a34a;
  `}
  
  ${props => props.status === 'needs-setup' && `
    background: #fffbeb;
    color: #d97706;
  `}
  
  ${props => props.status === 'unavailable' && `
    background: #fef2f2;
    color: #dc2626;
  `}
`;

const MethodDescription = styled.p`
  color: #6b7280;
  margin: 0 0 12px 0;
  font-size: 14px;
  line-height: 1.5;
`;

const MethodFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 12px 0 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Feature = styled.li`
  font-size: 12px;
  color: #6b7280;
  background: #f8fafc;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  align-items: center;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  
  ${props => props.variant === 'primary' ? `
    background: #3b82f6;
    color: white;
    border: 1px solid #3b82f6;
    
    &:hover {
      background: #2563eb;
      border-color: #2563eb;
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
    }
    
    &:disabled {
      background: #9ca3af;
      border-color: #9ca3af;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  ` : `
    background: transparent;
    color: #6b7280;
    border: 1px solid #e5e7eb;
    
    &:hover {
      background: #f3f4f6;
      color: #374151;
      transform: translateY(-1px);
    }
  `}
`;

// Enhanced language list - original language first, removed Auto Detect
const getLanguagesForTranscription = (originalLanguage = 'en') => {
  const allLanguages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'ko', name: '한국어', flag: '🇰🇷' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
    { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
    { code: 'da', name: 'Dansk', flag: '🇩🇰' },
    { code: 'no', name: 'Norsk', flag: '🇳🇴' },
    { code: 'fi', name: 'Suomi', flag: '🇫🇮' },
    { code: 'pl', name: 'Polski', flag: '🇵🇱' }
  ];

  // Find original language and put it first
  const originalLang = allLanguages.find(lang => lang.code === originalLanguage);
  const otherLanguages = allLanguages.filter(lang => lang.code !== originalLanguage);
  
  return originalLang ? [originalLang, ...otherLanguages] : allLanguages;
};

const TranscriptionMethodDialog = ({ 
  isOpen, 
  onClose, 
  onSelectMethod,
  availableMethods = {},
  selectedLanguage = 'en',
  originalLanguage = 'en'
}) => {
  const [selectedMethod, setSelectedMethod] = useState('whisper-local');
  const [transcriptionLanguage, setTranscriptionLanguage] = useState(selectedLanguage);
  const [enableSpeakerDetection, setEnableSpeakerDetection] = useState(true);
  const [speakerDetectionQuality, setSpeakerDetectionQuality] = useState('standard');
  const [localWhisperStatus, setLocalWhisperStatus] = useState('checking'); // 'checking', 'available', 'needs-setup'

  // Check Desktop Service and Local Whisper when dialog opens
  useEffect(() => {
    if (isOpen) {
      checkLocalWhisperAvailability();
    }
  }, [isOpen]);

  const checkLocalWhisperAvailability = async () => {
    try {
      console.log('🔍 Checking Local Whisper availability...');
      setLocalWhisperStatus('checking');
      
      const TranscriptionService = (await import('../services/transcriptionService')).default;
      const transcriptionService = new TranscriptionService({ whisper: null, pyannote: null });
      
      // Check Desktop Service
      console.log('🔍 Checking Desktop Service...');
      const serviceCheck = await transcriptionService.checkDesktopService();
      console.log('📡 Desktop Service check result:', serviceCheck);
      
      if (!serviceCheck.available) {
        console.warn('⚠️ Desktop Service not available');
        setLocalWhisperStatus('needs-setup');
        return;
      }
      
      // Check Local Whisper
      console.log('🔍 Checking Local Whisper...');
      const whisperCheck = await transcriptionService.checkLocalWhisper();
      console.log('🎤 Local Whisper check result:', whisperCheck);
      
      if (whisperCheck && whisperCheck.ready === true) {
        console.log('✅ Local Whisper is available');
        setLocalWhisperStatus('available');
      } else {
        console.warn('⚠️ Local Whisper not ready:', whisperCheck);
        setLocalWhisperStatus('needs-setup');
      }
    } catch (error) {
      console.error('❌ Error checking Local Whisper availability:', error);
      setLocalWhisperStatus('needs-setup');
    }
  };

  if (!isOpen) return null;

  const availableLanguages = getLanguagesForTranscription(originalLanguage);

  const methods = [
    {
      id: 'whisper-local',
      name: 'Local Whisper (Free)',
      icon: <HardDrive size={20} />,
      color: '#059669',
      status: localWhisperStatus === 'checking' ? 'checking' : (localWhisperStatus === 'available' ? 'available' : 'needs-setup'),
      description: 'Run Whisper AI locally on your computer. No internet required, completely free and private.',
      features: ['Free forever', 'Works offline', '100% private', 'No usage limits', 'Multiple languages', 'Basic speaker detection'],
      setupRequired: localWhisperStatus === 'available' ? null : 'Requires Python and Whisper installation'
    },
    {
      id: 'whisper-api',
      name: 'OpenAI Whisper API',
      icon: <Cloud size={20} />,
      color: '#3b82f6',
      status: availableMethods.whisper ? 'available' : 'needs-setup',
      description: 'Cloud-based transcription using OpenAI\'s Whisper API. High accuracy and fast processing.',
      features: ['High accuracy', 'Fast processing', 'Automatic language detection', 'Speaker timestamps', 'Advanced speaker detection'],
      setupRequired: availableMethods.whisper ? null : 'Requires OpenAI API key and credits'
    },
    {
      id: 'google-speech',
      name: 'Google Speech-to-Text',
      icon: <Globe size={20} />,
      color: '#ea4335',
      status: 'needs-setup',
      description: 'Google Cloud Speech-to-Text API with advanced features and multilingual support.',
      features: ['Excellent accuracy', 'Real-time transcription', '120+ languages', 'Noise filtering'],
      setupRequired: 'Requires Google Cloud API key'
    },
    {
      id: 'azure-speech',
      name: 'Azure Speech Services',
      icon: <Cloud size={20} />,
      color: '#0078d4',
      status: 'needs-setup',
      description: 'Microsoft Azure Speech Services with neural voice and custom models.',
      features: ['Neural networks', 'Custom models', 'Multi-speaker', 'Batch processing'],
      setupRequired: 'Requires Azure subscription'
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return <CheckCircle size={14} />;
      case 'needs-setup':
        return <AlertCircle size={14} />;
      case 'unavailable':
        return <X size={14} />;
      default:
        return <Info size={14} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'available':
        return 'Ready';
      case 'needs-setup':
        return 'Setup Required';
      case 'checking':
        return 'Checking...';
      case 'unavailable':
        return 'Unavailable';
      default:
        return 'Unknown';
    }
  };

  const handleContinue = () => {
    const method = methods.find(m => m.id === selectedMethod);
    
    // Include speaker detection settings
    const methodWithSpeakerSettings = {
      ...method,
      speakerDetection: {
        enabled: enableSpeakerDetection,
        quality: speakerDetectionQuality
      }
    };
    
    // Pass method, language, and speaker detection settings
    onSelectMethod(selectedMethod, methodWithSpeakerSettings, transcriptionLanguage);
  };

  const selectedMethodData = methods.find(m => m.id === selectedMethod);
  const selectedLangName = availableLanguages.find(l => l.code === transcriptionLanguage)?.name || transcriptionLanguage;
  const speakerDetectionText = enableSpeakerDetection ? ` + ${speakerDetectionQuality} speaker detection` : '';

  return (
    <Overlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <Modal>
        <Header>
          <Title>
            <Mic size={24} />
            Choose Transcription Method
          </Title>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </Header>

        <Content>
          <Description>
            Select how you want to transcribe your video and choose the audio language. Each method has different requirements, costs, and features.
          </Description>

          {/* Language Selection Section */}
          <LanguageSection>
            <LanguageTitle>🎤 Transcription Language</LanguageTitle>
            <LanguageSelector 
              value={transcriptionLanguage} 
              onChange={(e) => setTranscriptionLanguage(e.target.value)}
            >
              {availableLanguages.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </LanguageSelector>
          </LanguageSection>

          {/* Speaker Detection Section */}
          <SpeakerSection>
            <SpeakerToggle>
              <ToggleLabel>
                <Users size={16} />
                👥 Speaker Detection (Who is speaking?)
              </ToggleLabel>
              <ToggleSwitch
                type="checkbox"
                checked={enableSpeakerDetection}
                onChange={(e) => setEnableSpeakerDetection(e.target.checked)}
              />
            </SpeakerToggle>
            
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
              Automatically detect different speakers and separate segments by voice changes
            </div>

            <QualitySelector enabled={enableSpeakerDetection}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                Detection Quality:
              </div>
              
              <QualityOption 
                selected={speakerDetectionQuality === 'basic'}
                onClick={() => setSpeakerDetectionQuality('basic')}
              >
                <QualityRadio
                  type="radio"
                  name="speakerQuality"
                  value="basic"
                  checked={speakerDetectionQuality === 'basic'}
                  onChange={() => setSpeakerDetectionQuality('basic')}
                />
                <QualityDetails>
                  <QualityName>🟢 Basic (Free)</QualityName>
                  <QualityDescription>
                    Simple voice activity detection. Good for clear audio with few speakers.
                  </QualityDescription>
                </QualityDetails>
              </QualityOption>

              <QualityOption 
                selected={speakerDetectionQuality === 'standard'}
                onClick={() => setSpeakerDetectionQuality('standard')}
              >
                <QualityRadio
                  type="radio"
                  name="speakerQuality"
                  value="standard"
                  checked={speakerDetectionQuality === 'standard'}
                  onChange={() => setSpeakerDetectionQuality('standard')}
                />
                <QualityDetails>
                  <QualityName>🟡 Standard (Recommended)</QualityName>
                  <QualityDescription>
                    AI-powered speaker diarization. Works well for most conversations and meetings.
                  </QualityDescription>
                </QualityDetails>
              </QualityOption>

              <QualityOption 
                selected={speakerDetectionQuality === 'premium'}
                onClick={() => setSpeakerDetectionQuality('premium')}
              >
                <QualityRadio
                  type="radio"
                  name="speakerQuality"
                  value="premium"
                  checked={speakerDetectionQuality === 'premium'}
                  onChange={() => setSpeakerDetectionQuality('premium')}
                />
                <QualityDetails>
                  <QualityName>🔴 Premium (High Accuracy)</QualityName>
                  <QualityDescription>
                    Advanced neural networks. Best for complex audio with multiple speakers and background noise.
                  </QualityDescription>
                </QualityDetails>
              </QualityOption>
            </QualitySelector>
          </SpeakerSection>

          <MethodGrid>
            {methods.map((method) => (
              <MethodCard
                key={method.id}
                selected={selectedMethod === method.id}
                onClick={() => setSelectedMethod(method.id)}
              >
                <MethodHeader>
                  <MethodTitle>
                    <MethodIcon color={method.color}>
                      {method.icon}
                    </MethodIcon>
                    <MethodName>{method.name}</MethodName>
                  </MethodTitle>
                  <MethodStatus status={method.status}>
                    {getStatusIcon(method.status)}
                    {getStatusText(method.status)}
                  </MethodStatus>
                </MethodHeader>

                <MethodDescription>
                  {method.description}
                </MethodDescription>

                {method.setupRequired && (
                  <MethodDescription style={{ color: '#d97706', fontWeight: 500 }}>
                    ⚠️ {method.setupRequired}
                  </MethodDescription>
                )}

                <MethodFeatures>
                  {method.features.map((feature, index) => (
                    <Feature key={index}>{feature}</Feature>
                  ))}
                </MethodFeatures>
              </MethodCard>
            ))}
          </MethodGrid>
        </Content>

        <Actions>
          <Button onClick={onClose}>
            <X size={16} />
            Cancel
          </Button>
                      <Button 
              variant="primary" 
              onClick={handleContinue}
              disabled={!selectedMethodData}
            >
              <Mic size={16} />
              Start Transcription with {selectedMethodData?.name}
            </Button>
        </Actions>
      </Modal>
    </Overlay>
  );
};

export default TranscriptionMethodDialog; 